"use client";

import { useMemo, useState } from "react";
import { useEconomicCalendar } from "@/vybe-trader-client/economic-calendar";
import { useEconomicCalendarStore } from "@/vybe-trader-client/economic-calendar/stores/useEconomicCalendarStore";
import { buildEventsCountByDay, calendarDayKeyLocal } from "./selectors";

/** Selected local day → sorted events only (compact table UX). */
export function useEconomicCalendarViewModel() {
  const { allEvents, loadingStatus, getEventsByDate, isComplete } =
    useEconomicCalendar();
  const errorMessage = useEconomicCalendarStore((s) => s.errorMessage);

  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());

  const eventCountsByDay = useMemo(
    () => buildEventsCountByDay(allEvents),
    [allEvents]
  );

  const displayEvents = useMemo(() => {
    const dk = calendarDayKeyLocal(selectedDate);
    return [...getEventsByDate(dk)].sort(
      (a, b) =>
        new Date(a.dateTimeISO).getTime() - new Date(b.dateTimeISO).getTime()
    );
    // Recompute when the day changes or the dataset updates (allEvents identity changes on each flush).
  }, [selectedDate, getEventsByDate, allEvents]);

  // Only show the loading/empty state before ANY data has arrived. Background refreshes keep the
  // current events on screen (no blink) — the store updates in place and re-renders on the next flush.
  const awaitingData = allEvents.length === 0 && loadingStatus !== "error";

  return {
    loadingStatus,
    isComplete,
    awaitingData,
    errorMessage,

    selectedDate,
    setSelectedDate,

    allEvents,
    displayEvents,
    eventCountsByDay,
  };
}
